import { INVITE_ONLY_MODE } from '@archetelos/lib';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { sanitizeNextPath } from '@/lib/auth/next';
import { useAuth } from '@/lib/auth/useAuth';
import { profileQueries } from '@/lib/queries/profile';
import { supabase } from '@/lib/supabase';

const normalizeCode = (raw: string) => raw.replace(/-/g, '').trim().toUpperCase();

const signUpSchema = z
  .object({
    inviteCode: z.string().optional(),
    email: z.string().email('Enter a valid email'),
    username: z
      .string()
      .min(3, 'Username must be at least 3 characters')
      .max(30, 'Username must be 30 characters or fewer')
      .regex(
        /^[a-z0-9][a-z0-9_-]+$/,
        'Lowercase letters, numbers, hyphens, and underscores; must start with a letter or number',
      ),
    displayName: z.string().min(1, 'Required').max(60, 'Keep it under 60 characters'),
    password: z
      .string()
      .min(8, 'Use at least 8 characters')
      .regex(/[A-Z]/, 'Include at least one uppercase letter')
      .regex(/[0-9]/, 'Include at least one number'),
    confirmPassword: z.string(),
    accept: z.literal(true, { errorMap: () => ({ message: 'Required' }) }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type SignUpValues = z.infer<typeof signUpSchema>;

interface SignUpSearch {
  next?: string;
}

export const Route = createFileRoute('/auth/sign-up')({
  validateSearch: (search): SignUpSearch => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
  component: SignUpRoute,
});

function SignUpRoute() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { next: rawNext } = Route.useSearch();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      username: '',
      displayName: '',
      password: '',
      confirmPassword: '',
      accept: false as unknown as true,
    },
    mode: 'onBlur',
  });

  const usernameRaw = form.watch('username');
  const [debouncedUsername, setDebouncedUsername] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedUsername(usernameRaw ?? ''), 300);
    return () => window.clearTimeout(id);
  }, [usernameRaw]);

  const usernameQuery = useQuery({
    ...profileQueries.checkUsernameAvailable(debouncedUsername),
    enabled: debouncedUsername.length >= 3 && /^[a-z0-9][a-z0-9_-]+$/.test(debouncedUsername),
  });

  const [inviteState, setInviteState] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [inviteReason, setInviteReason] = useState<string | null>(null);

  const validateInvite = async (raw: string): Promise<boolean> => {
    const code = raw.trim();
    if (!code) {
      setInviteState('invalid');
      setInviteReason('An invite code is required.');
      return false;
    }
    setInviteState('checking');
    const compact = normalizeCode(code);
    const dashed =
      compact.length === 12
        ? `${compact.slice(0, 4)}-${compact.slice(4, 8)}-${compact.slice(8, 12)}`
        : code;
    const { data, error } = await supabase.rpc('validate_invite_code', {
      check_code: dashed,
    });
    const row = Array.isArray(data) ? data[0] : data;
    if (error || !row?.valid) {
      setInviteState('invalid');
      setInviteReason(
        row?.reason === 'already_used'
          ? 'That code has already been used.'
          : row?.reason === 'expired'
            ? 'That code has expired.'
            : 'That invite code was not found.',
      );
      return false;
    }
    setInviteState('valid');
    setInviteReason(null);
    return true;
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    if (usernameQuery.data === false) {
      form.setError('username', { message: 'That username is taken' });
      return;
    }
    let inviteCode: string | undefined;
    if (INVITE_ONLY_MODE) {
      const ok = await validateInvite(values.inviteCode ?? '');
      if (!ok) return;
      inviteCode = normalizeCode(values.inviteCode ?? '');
    }
    try {
      await signUp(
        values.email,
        values.password,
        {
          username: values.username,
          display_name: values.displayName,
          ...(inviteCode ? { invite_code: inviteCode } : {}),
        },
        rawNext,
      );
      // Carry next through verify so a re-sent link keeps the funnel intent.
      void navigate({ to: '/auth/verify', search: { email: values.email, next: rawNext } });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to create account');
    }
  });

  // Funnel-aware subtitle: a membership-bound sign-up (next → /subscribe) leads
  // with the reason they're here; everything else gets the plain promise.
  const subtitle = sanitizeNextPath(rawNext).startsWith('/subscribe')
    ? 'One step from membership — your account comes first.'
    : 'Read, watch, and contribute.';

  return (
    <AuthLayout title="Create an account" subtitle={subtitle}>
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {INVITE_ONLY_MODE && (
          <Field
            id="inviteCode"
            label="Invite code"
            autoComplete="off"
            placeholder="XXXX-XXXX-XXXX"
            error={inviteState === 'invalid' ? (inviteReason ?? 'Invalid code') : undefined}
            hint={
              inviteState === 'checking'
                ? 'Checking…'
                : inviteState === 'valid'
                  ? 'Valid invite'
                  : 'ArcheTelos is invite-only during launch.'
            }
            hintVariant={inviteState === 'valid' ? 'ok' : 'neutral'}
            {...form.register('inviteCode', {
              onBlur: (e) => {
                if (e.target.value.trim()) void validateInvite(e.target.value);
              },
            })}
          />
        )}
        <Field
          id="email"
          label="Email"
          type="email"
          autoComplete="email"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <Field
          id="username"
          label="Username"
          autoComplete="username"
          error={form.formState.errors.username?.message}
          hint={
            debouncedUsername.length >= 3
              ? usernameQuery.isFetching
                ? 'Checking availability…'
                : usernameQuery.data === false
                  ? 'That username is taken'
                  : usernameQuery.data === true
                    ? 'Available'
                    : undefined
              : undefined
          }
          hintVariant={
            usernameQuery.data === true ? 'ok' : usernameQuery.data === false ? 'error' : 'neutral'
          }
          {...form.register('username')}
        />
        <Field
          id="displayName"
          label="Display name"
          autoComplete="name"
          error={form.formState.errors.displayName?.message}
          {...form.register('displayName')}
        />
        <Field
          id="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <Field
          id="confirmPassword"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.confirmPassword?.message}
          {...form.register('confirmPassword')}
        />

        <label className="flex items-start gap-2 text-sm text-ink/80">
          <input
            type="checkbox"
            {...form.register('accept')}
            className="mt-1 h-4 w-4 rounded border-ink/30 text-oxblood focus:ring-oxblood"
          />
          <span>
            I agree to the{' '}
            <a
              href={`${import.meta.env.PUBLIC_MARKETING_URL}/legal/terms`}
              className="text-oxblood hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href={`${import.meta.env.PUBLIC_MARKETING_URL}/legal/privacy`}
              className="text-oxblood hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>
        {form.formState.errors.accept && (
          <p role="alert" className="text-xs text-signal">
            {form.formState.errors.accept.message}
          </p>
        )}

        {submitError && (
          <p role="alert" aria-live="polite" className="text-sm text-signal">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
        >
          {form.formState.isSubmitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-ink/70">
        Already a member?{' '}
        <Link to="/auth/sign-in" className="text-oxblood hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  hintVariant?: 'ok' | 'error' | 'neutral';
}
const Field = (() => {
  const C = ({ id, label, error, hint, hintVariant = 'neutral', ...rest }: FieldProps) => (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
      >
        {label}
      </label>
      <input
        id={id}
        {...rest}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-signal">
          {error}
        </p>
      )}
      {!error && hint && (
        <p
          id={`${id}-hint`}
          className={`mt-1 text-xs ${
            hintVariant === 'ok'
              ? 'text-emerald-700'
              : hintVariant === 'error'
                ? 'text-signal'
                : 'text-ink/50'
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
  return C;
})();
